/**
 * Socket Manager View
 * Real-time monitoring with software dropdown, email cards, VPS logs
 * Click VPS to open Modal with all events
 */
import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import {
    Row, Col, Card, CardBody, CardHeader,
    Label, Progress, Badge, Input, Button,
    Modal, ModalHeader, ModalBody, Collapse
} from 'reactstrap';
import Deco from '../security/decrypt';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
const ADMIN_EVENTS = {
    REQUEST_STATS: 'admin:request:stats',
    STATS_UPDATE: 'admin:stats:update',
    REQUEST_VPS_LOGS: 'admin:request:vps:logs',
    VPS_LOGS_UPDATE: 'admin:vps:logs:update',
    CLEAR_VPS_LOGS: 'admin:clear:vps:logs',
    SUBSCRIBE_LOGS: 'admin:subscribe:logs',
    UNSUBSCRIBE_LOGS: 'admin:unsubscribe:logs',
    NEW_LOG: 'admin:new:log'
};

const SocketManager = () => {
    const [connected, setConnected] = useState(false);
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [selectedSoftware, setSelectedSoftware] = useState('');

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedVps, setSelectedVps] = useState(null);
    const [vpsLogs, setVpsLogs] = useState([]);
    const [expandedLogs, setExpandedLogs] = useState({});

    const socketRef = useRef(null);

    useEffect(() => {
        try {
            const user = JSON.parse(Deco(localStorage.getItem('user')));
            const token = user?.token;
            if (!token) {
                setError('No token');
                return;
            }

            socketRef.current = io(`${SOCKET_URL}/admin`, {
                auth: { token },
                transports: ['websocket', 'polling']
            });

            socketRef.current.on('connect', () => {
                setConnected(true);
                setError(null);
            });

            socketRef.current.on(ADMIN_EVENTS.STATS_UPDATE, (data) => {
                setStats(data);
                setLastUpdate(new Date());
                if (!selectedSoftware && data.softwareList?.length > 0) {
                    const firstWithData = data.softwareList.find(sw =>
                        data.dataBySoftware?.[sw.id]?.totalVps > 0
                    );
                    if (firstWithData) {
                        setSelectedSoftware(firstWithData.id);
                    } else {
                        setSelectedSoftware(data.softwareList[0].id);
                    }
                }
            });

            socketRef.current.on(ADMIN_EVENTS.VPS_LOGS_UPDATE, (data) => {
                setVpsLogs(data.logs || []);
            });

            // Listen for new logs in real-time
            socketRef.current.on(ADMIN_EVENTS.NEW_LOG, (data) => {
                setVpsLogs(prev => [...prev, data.log].slice(-50)); // Keep max 50 logs
            });

            socketRef.current.on('connect_error', (err) => {
                setConnected(false);
                setError(err.message);
            });

            socketRef.current.on('disconnect', () => setConnected(false));

        } catch (err) {
            setError('Auth error');
        }

        return () => socketRef.current?.disconnect();
    }, []);

    // Open modal and request logs
    const openVpsModal = (vps) => {
        setSelectedVps(vps);
        setModalOpen(true);
        setExpandedLogs({});
        if (socketRef.current) {
            socketRef.current.emit(ADMIN_EVENTS.REQUEST_VPS_LOGS, { key: vps.username });
            // Subscribe for real-time updates
            socketRef.current.emit(ADMIN_EVENTS.SUBSCRIBE_LOGS, { key: vps.username });
        }
    };

    // Close modal and unsubscribe
    const closeVpsModal = () => {
        if (socketRef.current && selectedVps) {
            socketRef.current.emit(ADMIN_EVENTS.UNSUBSCRIBE_LOGS, { key: selectedVps.username });
        }
        setModalOpen(false);
    };

    // Toggle log expansion
    const toggleLogExpand = (idx) => {
        setExpandedLogs(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    // Format bytes
    const formatBytes = (bytes) => {
        if (!bytes) return '0 B';
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
    };

    // Format uptime
    const formatUptime = (seconds) => {
        if (!seconds) return '0s';
        const d = Math.floor(seconds / 86400);
        const h = Math.floor((seconds % 86400) / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return `${d > 0 ? d + 'd ' : ''}${h > 0 ? h + 'h ' : ''}${m}m`;
    };

    // Format JSON for readability
    const formatData = (data) => {
        if (!data) return 'No data';
        try {
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            return JSON.stringify(parsed, null, 2);
        } catch {
            return data;
        }
    };

    // Extract requestId from log data
    const extractRequestId = (log) => {
        // First try direct requestId property (new backend format)
        if (log.requestId) return log.requestId;

        // Fallback: extract from data (old format compatibility)
        if (!log.data) return null;
        try {
            const parsed = typeof log.data === 'string' ? JSON.parse(log.data) : log.data;
            return parsed?.requestId || null;
        } catch {
            return null;
        }
    };

    // Map request events to their expected response events
    const REQUEST_RESPONSE_MAP = {
        'dhbstream:screen:get-screens': 'dhbstream:screen:screens-list',
        'dhbstream:screen:capture-screens': 'dhbstream:screen:capture-result',
        'dhbstream:screen:get-windows': 'dhbstream:screen:windows-list',
        'dhbstream:audio:get-devices': 'dhbstream:audio:devices-list',
        'dhbstream:audio:get-apps': 'dhbstream:audio:apps-list',
        'dhbstream:render-config:get-list': 'dhbstream:render-config:list-response',
        'dhbstream:file-browser:get-drives': 'dhbstream:file-browser:drives-response',
        'dhbstream:file-browser:get-folder': 'dhbstream:file-browser:folder-response',
        'dhbstream:file-browser:get-special-folder': 'dhbstream:file-browser:special-folder-response',
        // Stream edit/log events (web:stream prefix)
        'dhbstream:web:stream:get': 'dhbstream:stream:data',
        'dhbstream:web:stream:log:get': 'dhbstream:stream:log:data',
        // Also map the VPS side stream:get (in case VPS sends different event)
        'dhbstream:stream:get': 'dhbstream:stream:data',
        'dhbstream:stream:log:get': 'dhbstream:stream:log:data',
    };

    // Build reverse map (response -> request)
    const RESPONSE_REQUEST_MAP = Object.fromEntries(
        Object.entries(REQUEST_RESPONSE_MAP).map(([req, res]) => [res, req])
    );

    // Group logs by matching request/response
    // Strategy: 
    // 1. If both have requestId - match by requestId
    // 2. If request has no requestId but response has - match by event name pattern + timing
    const groupLogsByRequestId = (logs) => {
        const grouped = [];
        const processedIndices = new Set();

        // Sort logs by timestamp (oldest first for processing)
        const sortedLogs = [...logs].sort((a, b) =>
            new Date(a.timestamp) - new Date(b.timestamp)
        );

        sortedLogs.forEach((log, idx) => {
            if (processedIndices.has(idx)) return;

            // Skip connection logs - don't group them
            if (log.type === 'connection') {
                grouped.push({ type: 'connection', log });
                processedIndices.add(idx);
                return;
            }

            const requestId = extractRequestId(log);

            // Check if this is a request (web-request)
            if (log.socketType === 'web-request') {
                const expectedResponseEvent = REQUEST_RESPONSE_MAP[log.eventName];

                if (expectedResponseEvent) {
                    // Look for matching response
                    let matchedResponseIdx = -1;

                    // First try to match by requestId (if present)
                    if (requestId) {
                        for (let j = idx + 1; j < sortedLogs.length; j++) {
                            if (processedIndices.has(j)) continue;
                            const potentialResponse = sortedLogs[j];
                            const responseRequestId = extractRequestId(potentialResponse);
                            if (responseRequestId === requestId &&
                                potentialResponse.eventName === expectedResponseEvent) {
                                matchedResponseIdx = j;
                                break;
                            }
                        }
                    }

                    // If no match by requestId, try by event name pattern (within 5 second window)
                    if (matchedResponseIdx === -1) {
                        const requestTime = new Date(log.timestamp).getTime();
                        for (let j = idx + 1; j < sortedLogs.length; j++) {
                            if (processedIndices.has(j)) continue;
                            const potentialResponse = sortedLogs[j];
                            const responseTime = new Date(potentialResponse.timestamp).getTime();

                            // Only match within 5 second window
                            if (responseTime - requestTime > 5000) break;

                            if (potentialResponse.eventName === expectedResponseEvent &&
                                potentialResponse.socketType !== 'web-request') {
                                matchedResponseIdx = j;
                                break;
                            }
                        }
                    }

                    if (matchedResponseIdx !== -1) {
                        const responseLog = sortedLogs[matchedResponseIdx];
                        grouped.push({
                            type: 'paired',
                            requestId: requestId || extractRequestId(responseLog) || 'event-match',
                            request: log,
                            response: responseLog,
                            timestamp: log.timestamp,
                            eventName: log.eventName
                        });
                        processedIndices.add(idx);
                        processedIndices.add(matchedResponseIdx);
                        return;
                    }
                }

                // No match found - standalone request (pending)
                grouped.push({
                    type: 'paired',
                    requestId: requestId || 'pending',
                    request: log,
                    response: null,
                    timestamp: log.timestamp,
                    eventName: log.eventName
                });
                processedIndices.add(idx);
                return;
            }

            // This is a response without a matching request
            // Check if it matches any known response pattern
            const expectedRequestEvent = RESPONSE_REQUEST_MAP[log.eventName];
            if (expectedRequestEvent) {
                // Look backwards for unprocessed request
                for (let j = idx - 1; j >= 0; j--) {
                    if (processedIndices.has(j)) continue;
                    const potentialRequest = sortedLogs[j];
                    if (potentialRequest.eventName === expectedRequestEvent &&
                        potentialRequest.socketType === 'web-request') {
                        const requestTime = new Date(potentialRequest.timestamp).getTime();
                        const responseTime = new Date(log.timestamp).getTime();
                        // Only match within 5 second window
                        if (responseTime - requestTime <= 5000) {
                            grouped.push({
                                type: 'paired',
                                requestId: requestId || extractRequestId(potentialRequest) || 'event-match',
                                request: potentialRequest,
                                response: log,
                                timestamp: potentialRequest.timestamp,
                                eventName: potentialRequest.eventName
                            });
                            processedIndices.add(idx);
                            processedIndices.add(j);
                            return;
                        }
                    }
                }
            }

            // Standalone response (no matching request)
            grouped.push({ type: 'standalone', log });
            processedIndices.add(idx);
        });

        // Sort by timestamp descending (newest first)
        grouped.sort((a, b) => {
            const timeA = a.timestamp || a.log?.timestamp;
            const timeB = b.timestamp || b.log?.timestamp;
            return new Date(timeB) - new Date(timeA);
        });

        return grouped;
    };

    // Render connection status
    const renderStatus = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Badge color={connected ? 'success' : 'danger'} style={{ fontSize: '14px', padding: '8px 16px' }}>
                {connected ? '🟢 Connected' : '🔴 Disconnected'}
            </Badge>
            {lastUpdate && <span style={{ color: '#666', fontSize: '14px' }}>Updated: {lastUpdate.toLocaleTimeString()}</span>}
            {error && <Badge color="warning" style={{ fontSize: '13px' }}>⚠️ {error}</Badge>}
        </div>
    );

    // Render system stats
    const renderSystem = () => {
        if (!stats?.system) return null;
        const sys = stats.system;
        return (
            <Card style={{ marginBottom: '20px', borderRadius: '20px' }}>
                <CardBody style={{ padding: '16px 20px' }}>
                    <Row>
                        <Col md="3">
                            <Label style={{ fontSize: '15px', fontWeight: 'bold', color: '#333' }}>🖥️ {sys.hostname}</Label>
                        </Col>
                        <Col md="3">
                            <div style={{ marginBottom: '5px' }}>
                                <span style={{ fontSize: '14px', color: '#666' }}>CPU: </span>
                                <span style={{ fontSize: '15px', fontWeight: 'bold' }}>{sys.cpuUsageAvg}%</span>
                            </div>
                            <Progress value={sys.cpuUsageAvg} color={sys.cpuUsageAvg > 80 ? 'danger' : 'info'} style={{ height: '8px' }} />
                        </Col>
                        <Col md="3">
                            <div style={{ marginBottom: '5px' }}>
                                <span style={{ fontSize: '14px', color: '#666' }}>RAM: </span>
                                <span style={{ fontSize: '15px', fontWeight: 'bold' }}>{sys.memoryUsagePercent}%</span>
                            </div>
                            <Progress value={sys.memoryUsagePercent} color={sys.memoryUsagePercent > 80 ? 'danger' : 'success'} style={{ height: '8px' }} />
                        </Col>
                        <Col md="3">
                            <span style={{ fontSize: '14px', color: '#666' }}>Uptime: </span>
                            <span style={{ fontSize: '15px' }}>{formatUptime(sys.uptime)}</span>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        );
    };

    // Render software dropdown
    const renderSoftwareDropdown = () => {
        if (!stats?.softwareList) return null;
        return (
            <Card style={{ marginBottom: '20px', borderRadius: '20px' }}>
                <CardBody style={{ padding: '16px 20px' }}>
                    <Row>
                        <Col md="4">
                            <Label style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '15px' }}>📦 Software</Label>
                            <Input
                                type="select"
                                value={selectedSoftware}
                                onChange={e => setSelectedSoftware(e.target.value)}
                                style={{ fontSize: '15px', padding: '10px' }}
                            >
                                {stats.softwareList.map(sw => (
                                    <option key={sw.id} value={sw.id}>{sw.name}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col md="8" style={{ display: 'flex', alignItems: 'flex-end', gap: '30px' }}>
                            {selectedSoftware && stats.dataBySoftware?.[selectedSoftware] && (
                                <>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#51cbce' }}>
                                            {stats.dataBySoftware[selectedSoftware].totalVps || 0}
                                        </div>
                                        <span style={{ color: '#666', fontSize: '14px' }}>VPS</span>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#6bd098' }}>
                                            {Object.keys(stats.dataBySoftware[selectedSoftware].emails || {}).length}
                                        </div>
                                        <span style={{ color: '#666', fontSize: '14px' }}>Emails</span>
                                    </div>
                                </>
                            )}
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        );
    };

    // Render email cards
    const renderEmailCards = () => {
        if (!selectedSoftware || !stats?.dataBySoftware?.[selectedSoftware]) return null;
        const softwareData = stats.dataBySoftware[selectedSoftware];
        const emails = Object.entries(softwareData.emails || {});

        if (emails.length === 0) {
            return <Card style={{ padding: '30px', textAlign: 'center', color: '#999', fontSize: '16px' }}>No connections</Card>;
        }

        return (
            <Row>
                {emails.map(([email, emailData]) => (
                    <Col md="6" lg="4" key={email} style={{ marginBottom: '20px' }}>
                        <Card style={{ borderRadius: '20px', height: '100%' }}>
                            <CardHeader style={{ padding: '12px 18px', backgroundColor: '#f8f9fa' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Label style={{ fontSize: '15px', fontWeight: 'bold', margin: 0 }}>
                                        📧 {email}
                                    </Label>
                                    <Badge color="primary" style={{ fontSize: '13px' }}>{emailData.vpsCount} VPS</Badge>
                                </div>
                            </CardHeader>
                            <CardBody style={{ padding: '12px 18px', maxHeight: '350px', overflowY: 'auto' }}>
                                {emailData.vpsList?.map((vps, idx) => (
                                    <div
                                        key={idx}
                                        onClick={() => openVpsModal(vps)}
                                        style={{
                                            padding: '12px 14px',
                                            marginBottom: '10px',
                                            backgroundColor: '#fafafa',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            border: '1px solid #eee',
                                            transition: 'background 0.2s'
                                        }}
                                        onMouseOver={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                        onMouseOut={e => e.currentTarget.style.backgroundColor = '#fafafa'}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <span style={{ fontSize: '15px', fontWeight: 'bold' }}>🖥️ {vps.vpsName}</span>
                                                <div style={{ fontSize: '13px', color: '#888' }}>{vps.username}</div>
                                            </div>
                                            <span style={{ fontSize: '18px', color: '#999' }}>▶</span>
                                        </div>
                                        {/* Recent logs summary */}
                                        {vps.recentLogs?.length > 0 && (
                                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                                                {vps.recentLogs.slice(-2).map((log, i) => (
                                                    <div key={i}>
                                                        {log.action === 'connected' ? '🟢' : '🔴'}
                                                        {' '}{log.socketType} {log.action}
                                                        <span style={{ color: '#aaa', marginLeft: '8px' }}>
                                                            {new Date(log.timestamp).toLocaleTimeString()}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {/* Web clients */}
                                {emailData.webClients?.map((client, idx) => (
                                    <div key={`web-${idx}`} style={{
                                        padding: '10px 14px',
                                        marginBottom: '8px',
                                        backgroundColor: '#e8f4f8',
                                        borderRadius: '8px',
                                        fontSize: '14px'
                                    }}>
                                        🌐 Web: {client.accounts?.join(', ') || client.email}
                                    </div>
                                ))}
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>
        );
    };

    // Render modal with all logs - 80% height
    const renderModal = () => (
        <Modal
            isOpen={modalOpen}
            toggle={closeVpsModal}
            size="xl"
            style={{ maxWidth: '50%', maxHeight: '90%' }}
        >
            <ModalHeader toggle={closeVpsModal} style={{ fontSize: '18px' }}>
                🖥️ {selectedVps?.vpsName} - {selectedVps?.username}
            </ModalHeader>
            <ModalBody style={{ height: '80vh', overflowY: 'auto', padding: '20px' }}>
                <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <Button color="info" size="sm" onClick={() => {
                        if (socketRef.current && selectedVps) {
                            socketRef.current.emit(ADMIN_EVENTS.REQUEST_VPS_LOGS, { key: selectedVps.username });
                        }
                    }}>
                        <i className="nc-icon nc-refresh-69" /> Refresh
                    </Button>
                    <Button color="danger" size="sm" onClick={() => {
                        if (socketRef.current && selectedVps) {
                            socketRef.current.emit(ADMIN_EVENTS.CLEAR_VPS_LOGS, { key: selectedVps.username });
                            setVpsLogs([]); // Clear local logs immediately
                        }
                    }}>
                        <i className="nc-icon nc-simple-remove" /> Clear All
                    </Button>
                    <span style={{ fontSize: '15px', color: '#666' }}>{vpsLogs.length} logs (max 50)</span>
                </div>

                {
                    vpsLogs.length === 0 ? (
                        <p style={{ color: '#999', textAlign: 'center', fontSize: '16px' }}>No logs available</p>
                    ) : (
                        <div>
                            {groupLogsByRequestId(vpsLogs).map((item, idx) => {
                                // Connection log
                                if (item.type === 'connection') {
                                    const log = item.log;
                                    return (
                                        <div key={idx} style={{ marginBottom: '10px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: '8px' }}>
                                            <div onClick={() => toggleLogExpand(idx)} style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <Badge color={log.action === 'connected' ? 'success' : 'danger'} style={{ fontSize: '12px' }}>
                                                        {log.action === 'connected' ? '🟢 CONNECTED' : '🔴 DISCONNECTED'}
                                                    </Badge>
                                                    <Badge color="secondary" style={{ fontSize: '12px' }}>{log.socketType}</Badge>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <span style={{ color: '#888', fontSize: '14px' }}>{new Date(log.timestamp).toLocaleTimeString()}</span>
                                                    <span style={{ fontSize: '16px' }}>{expandedLogs[idx] ? '▼' : '▶'}</span>
                                                </div>
                                            </div>
                                            <Collapse isOpen={expandedLogs[idx]}>
                                                <div style={{ padding: '15px 18px', backgroundColor: '#1e1e1e', color: '#e0e0e0', fontFamily: 'Consolas, Monaco, monospace', fontSize: '13px', borderTop: '1px solid #ddd' }}>
                                                    <div><span style={{ color: '#9cdcfe' }}>Tool:</span> {log.tool}</div>
                                                    <div><span style={{ color: '#9cdcfe' }}>Email:</span> {log.email || 'N/A'}</div>
                                                    <div><span style={{ color: '#9cdcfe' }}>Socket ID:</span> {log.socketId}</div>
                                                    {log.reason && <div><span style={{ color: '#9cdcfe' }}>Reason:</span> {log.reason}</div>}
                                                </div>
                                            </Collapse>
                                        </div>
                                    );
                                }

                                // Paired request/response log
                                if (item.type === 'paired') {
                                    const reqEvent = item.request?.eventName || '(pending)';
                                    const resEvent = item.response?.eventName || '(pending)';
                                    return (
                                        <div key={idx} style={{ marginBottom: '10px', backgroundColor: '#fff', border: '1px solid #17a2b8', borderRadius: '8px' }}>
                                            <div onClick={() => toggleLogExpand(idx)} style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <Badge color="info" style={{ fontSize: '12px' }}>🌐 Web → Tool</Badge>
                                                    <span style={{ fontSize: '14px', fontFamily: 'monospace', fontWeight: 'bold' }}>{reqEvent}</span>
                                                    {item.response && (
                                                        <>
                                                            <span style={{ color: '#888' }}>→</span>
                                                            <span style={{ fontSize: '14px', fontFamily: 'monospace', color: '#28a745' }}>{resEvent}</span>
                                                        </>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <span style={{ color: '#888', fontSize: '14px' }}>{new Date(item.timestamp).toLocaleTimeString()}</span>
                                                    <span style={{ fontSize: '16px' }}>{expandedLogs[idx] ? '▼' : '▶'}</span>
                                                </div>
                                            </div>
                                            <Collapse isOpen={expandedLogs[idx]}>
                                                <div style={{ borderTop: '1px solid #ddd' }}>
                                                    {/* Request Section */}
                                                    <div style={{ padding: '12px 18px', backgroundColor: '#e3f2fd' }}>
                                                        <div style={{ fontWeight: 'bold', color: '#1976d2', marginBottom: '8px' }}>📥 REQUEST: {reqEvent}</div>
                                                        <div style={{ backgroundColor: '#1e1e1e', color: '#e0e0e0', fontFamily: 'Consolas, Monaco, monospace', fontSize: '13px', padding: '10px', borderRadius: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '200px', overflowY: 'auto' }}>
                                                            {item.request ? formatData(item.request.data) : '(No request data)'}
                                                        </div>
                                                    </div>
                                                    {/* Response Section */}
                                                    <div style={{ padding: '12px 18px', backgroundColor: '#e8f5e9' }}>
                                                        <div style={{ fontWeight: 'bold', color: '#388e3c', marginBottom: '8px' }}>📤 RESPONSE: {resEvent}</div>
                                                        <div style={{ backgroundColor: '#1e1e1e', color: '#e0e0e0', fontFamily: 'Consolas, Monaco, monospace', fontSize: '13px', padding: '10px', borderRadius: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '200px', overflowY: 'auto' }}>
                                                            {item.response ? formatData(item.response.data) : '(Waiting for response...)'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Collapse>
                                        </div>
                                    );
                                }

                                // Standalone log (no requestId)
                                const log = item.log;
                                return (
                                    <div key={idx} style={{ marginBottom: '10px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px' }}>
                                        <div onClick={() => toggleLogExpand(idx)} style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <Badge color={log.socketType === 'web-request' ? 'info' : 'success'} style={{ fontSize: '12px' }}>
                                                    {log.socketType === 'web-request' ? '🌐 Web → Tool' : '📤 Tool → Web'}
                                                </Badge>
                                                <span style={{ fontSize: '14px', fontFamily: 'monospace', fontWeight: 'bold' }}>{log.eventName}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <span style={{ color: '#888', fontSize: '14px' }}>{new Date(log.timestamp).toLocaleTimeString()}</span>
                                                <span style={{ fontSize: '16px' }}>{expandedLogs[idx] ? '▼' : '▶'}</span>
                                            </div>
                                        </div>
                                        <Collapse isOpen={expandedLogs[idx]}>
                                            <div style={{ padding: '15px 18px', backgroundColor: '#1e1e1e', color: '#e0e0e0', fontFamily: 'Consolas, Monaco, monospace', fontSize: '13px', borderTop: '1px solid #ddd', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '300px', overflowY: 'auto' }}>
                                                {formatData(log.data)}
                                            </div>
                                        </Collapse>
                                    </div>
                                );
                            })}
                        </div>
                    )
                }
            </ModalBody >
        </Modal >
    );

    return (
        <div className="content" style={{ fontSize: '15px' }}>
            {renderStatus()}
            {renderSystem()}
            {renderSoftwareDropdown()}
            {renderEmailCards()}
            {renderModal()}
        </div>
    );
};

export default SocketManager;
